import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Layer, Stage } from 'react-konva';
import { useNavigate } from 'react-router-dom';

import { PATH } from 'app/constants/path';

import { ChangeColorComp } from './ChangeColorComp';
import { RackRect } from './RackRect';
import { RoomRect } from './RoomRect';
import TransformerComponent from './TranformerComp';
import { IRect } from './core/type';
import Konva from 'konva';

export interface LocationMapEditorRef {
  getStageData: () => any;
}

export const LocationMapEditor = forwardRef<
  LocationMapEditorRef,
  {
    width: number;
    height: number;
    rooms: IRect[];
    racks: IRect[];
    onSaveLayout?: (data: any) => void;
    setIsLayoutChanged?: (isChanged: boolean) => void;
    handleDeleteRack: (id: string) => void;
    isEditMode: boolean;
    onOutOfBoundsDrag?: (isOverlap: boolean) => void;
    onUpdateRackPosition?: (id: string, x: number, y: number) => void;
    onUpdateRoomPosition?: (id: string, x: number, y: number) => void;
    onDragEndRefresh?: () => void;
  }
>(
  (
    {
      width,
      height,
      rooms,
      racks,
      onSaveLayout,
      setIsLayoutChanged,
      handleDeleteRack,
      isEditMode,
      onOutOfBoundsDrag,
      onUpdateRackPosition,
      onUpdateRoomPosition,
      onDragEndRefresh,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const stageRef = useRef<Konva.Stage>(null);
    const [selectedShapeName, setSelectedShapeName] = useState('');
    const [selectedDetailName, setSelectedDetailName] = useState('');
    const initialPositionsRef = useRef<
      Map<
        string,
        {
          x: number;
          y: number;
          width: number;
          height: number;
          scaleX: number;
          scaleY: number;
          offsetX: number;
          offsetY: number;
          rotation: number;
          skewX: number;
          skewY: number;
          rectAttrs: any;
          textAttrs: any;
        }
      >
    >(new Map());

    useImperativeHandle(ref, () => ({
      getStageData: () => {
        return stageRef?.current?.getStage()?.toJSON();
      },
    }));

    useEffect(() => {
      const setPropertyItem = (item: IRect) => ({
        x: item.x,
        y: item.y,
        width: item.width || 0,
        height: item.height || 0,
        scaleX: item.scaleX || 1,
        scaleY: item.scaleY || 1,
        offsetX: item.offsetX || 0,
        offsetY: item.offsetY || 0,
        rotation: item.rotation || 0,
        skewX: item.skewX || 0,
        skewY: item.skewY || 0,
        rectAttrs: item.rectAttrs,
        textAttrs: item.textAttrs,
      });

      // Add racks to initial positions
      racks.forEach((rack) => {
        if (!initialPositionsRef.current.has(rack.id))
          initialPositionsRef.current.set(rack.id, setPropertyItem(rack));
      });

      // Add rooms to initial positions
      rooms.forEach((room) => {
        if (!initialPositionsRef.current.has(room.id))
          initialPositionsRef.current.set(room.id, setPropertyItem(room));
      });

      // Clean up removed racks or rooms
      const currentIds = new Set([
        ...racks.map((rack) => rack.id),
        ...rooms.map((room) => room.id),
      ]);
      initialPositionsRef.current.forEach((_, id) => {
        if (!currentIds.has(id)) initialPositionsRef.current.delete(id);
      });
    }, [racks, rooms]);

    useEffect(() => {
      if (stageRef.current && rooms.length > 0) {
        const timer = setTimeout(() => {
          adjustTextScales();
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [rooms, racks]);

    const isOutsideStageBoundaries = (
      boundingBox: { x: number; y: number; width: number; height: number },
      stage: Konva.Stage,
    ): boolean =>
      boundingBox.x < 0 ||
      boundingBox.y < 0 ||
      boundingBox.x + boundingBox.width > stage.width() ||
      boundingBox.y + boundingBox.height > stage.height();

    const getBoundsFromBox = (box: { x: number; y: number; width: number; height: number }) => ({
      x1: box.x,
      y1: box.y,
      x2: box.x + box.width,
      y2: box.y + box.height,
    });

    const doOverlap = (
      bounds1: { x1: number; y1: number; x2: number; y2: number },
      bounds2: { x1: number; y1: number; x2: number; y2: number },
    ): boolean =>
      bounds1.x1 < bounds2.x2 &&
      bounds1.x2 > bounds2.x1 &&
      bounds1.y1 < bounds2.y2 &&
      bounds1.y2 > bounds2.y1;

    const checkRackOverlapsWithRacks = (
      targetId: string,
      targetBounds: { x1: number; y1: number; x2: number; y2: number },
      items: IRect[],
      stage: Konva.Stage,
    ): boolean => {
      for (const rack of items) {
        if (rack.id === targetId) continue; // Skip the object itself

        const rackNode = stage.findOne(`#${rack.id}`);
        if (!rackNode) continue;

        const rackBounds = getBoundsFromBox(rackNode.getClientRect());
        if (doOverlap(targetBounds, rackBounds)) return true;
      }
      return false;
    };

    const checkRackOverlapsWithRoomEdges = (
      targetBounds: { x1: number; y1: number; x2: number; y2: number },
      items: IRect[],
      stage: Konva.Stage,
    ): boolean => {
      for (const room of items) {
        const roomNode = stage.findOne(`#${room.id}`);
        if (!roomNode) continue;

        const roomBounds = getBoundsFromBox(roomNode.getClientRect());

        // Check if rack is partially inside the room (overlapping with edges)
        const isPartiallyInside = doOverlap(targetBounds, roomBounds);

        const isCompletelyInside =
          targetBounds.x1 >= roomBounds.x1 &&
          targetBounds.x2 <= roomBounds.x2 &&
          targetBounds.y1 >= roomBounds.y1 &&
          targetBounds.y2 <= roomBounds.y2;

        // If rack is partially inside but not completely inside, it's overlapping with the edge
        if (isPartiallyInside && !isCompletelyInside) return true;
      }
      return false;
    };

    const checkRoomOverlapsWithRooms = (
      targetId: string,
      targetBounds: { x1: number; y1: number; x2: number; y2: number },
      items: IRect[],
      stage: Konva.Stage,
    ): boolean => {
      for (const room of items) {
        if (room.id === targetId) continue; // Skip the object itself

        const roomNode = stage.findOne(`#${room.id}`);
        if (!roomNode) continue;

        const roomBounds = getBoundsFromBox(roomNode.getClientRect());
        if (doOverlap(targetBounds, roomBounds)) return true;
      }
      return false;
    };

    const checkForOverlaps = (
      targetId: string,
      targetBounds: { x1: number; y1: number; x2: number; y2: number },
      isRack: boolean,
      roomItems: IRect[],
      rackItems: IRect[],
      stage: Konva.Stage,
    ): boolean => {
      if (isRack) {
        // Check overlap with other racks
        if (checkRackOverlapsWithRacks(targetId, targetBounds, rackItems, stage)) return true;

        // Check overlap with room edges
        return checkRackOverlapsWithRoomEdges(targetBounds, roomItems, stage);
      } else {
        // Check overlap with other rooms
        return checkRoomOverlapsWithRooms(targetId, targetBounds, roomItems, stage);
      }
    };

    const resetToInitialPosition = (
      node: any,
      initialPos: {
        x: number;
        y: number;
        scaleX: number;
        scaleY: number;
        offsetX: number;
        offsetY: number;
        rotation: number;
        textAttrs: any;
        rectAttrs: any;
      },
      stage: Konva.Stage,
      uppdateRackPosition?: (id: string, x: number, y: number) => void,
      updateRoomPosition?: (id: string, x: number, y: number) => void,
    ) => {
      const id = node.id();

      // Reset position and transformation
      node.position({
        x: initialPos.x,
        y: initialPos.y,
      });

      node.scale({
        x: initialPos.scaleX,
        y: initialPos.scaleY,
      });

      node.offset({
        x: initialPos.offsetX,
        y: initialPos.offsetY,
      });

      node.rotation(initialPos.rotation);

      // Reset text element if this is a group
      if (node.getClassName() === 'Group') {
        const textNode = node.findOne('Text');
        if (textNode) {
          // Reset text attributes
          if (initialPos.textAttrs) textNode.setAttrs(initialPos.textAttrs);

          textNode.scaleX(1 / initialPos.scaleX);
          textNode.scaleY(1 / initialPos.scaleY);
        }

        const rectNode = node.findOne('Rect');
        if (rectNode && initialPos.rectAttrs) rectNode.setAttrs(initialPos.rectAttrs);
      }

      // Update state to reflect the rollback
      const isRack = id.startsWith('rack-');
      if (isRack && uppdateRackPosition) uppdateRackPosition(id, initialPos.x, initialPos.y);
      if (updateRoomPosition) updateRoomPosition(id, initialPos.x, initialPos.y);

      // Clear flags
      node.setAttr('outsideBoundaries', false);
      node.setAttr('isOverlapping', false);

      stage.batchDraw();
    };

    const verticalAlignTextCenterWithEllipsis = (
      textNode: Konva.Text,
      rectNode: Konva.Rect,
      scaleX: number,
    ) => {
      textNode.ellipsis(true);
      textNode.wrap('none');

      if (rectNode) {
        const rectWidth = rectNode.width() * scaleX;

        textNode.width(rectWidth);
        textNode.align('center');
        textNode.verticalAlign('middle');
        textNode.y(rectNode.height() / 2 - textNode.height() / 2);
        textNode.x(0);
      }
    };

    const handleDragMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = stageRef.current?.getStage();
      if (!stage) return;

      const target = e.target;
      const boundingBox = target.getClientRect();
      const targetId = target.id();
      const isRack = targetId.startsWith('rack-');

      // Update position in state
      if (isRack && onUpdateRackPosition) onUpdateRackPosition(targetId, target.x(), target.y());
      if (onUpdateRoomPosition) onUpdateRoomPosition(targetId, target.x(), target.y());

      // Check if outside boundaries
      const isOutsideBoundaries = isOutsideStageBoundaries(boundingBox, stage);
      target.setAttr('outsideBoundaries', isOutsideBoundaries);

      // Check for overlaps
      const targetBounds = getBoundsFromBox(boundingBox);
      const isOverlapping = checkForOverlaps(targetId, targetBounds, isRack, rooms, racks, stage);
      target.setAttr('isOverlapping', isOverlapping);
    };

    const handleDragEnd = (e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = stageRef.current?.getStage();
      if (!stage) return;

      const target = e.target;
      const isOutside = target.getAttr('outsideBoundaries');
      const isOverlapping = target.getAttr('isOverlapping');

      if (isOutside || isOverlapping) {
        const id = target.id();
        const initialPos = initialPositionsRef.current.get(id);

        if (initialPos) {
          resetToInitialPosition(
            target,
            initialPos,
            stage,
            onUpdateRackPosition,
            onUpdateRoomPosition,
          );
          if (onOutOfBoundsDrag) onOutOfBoundsDrag(isOverlapping);
          return;
        }
      }

      if (onDragEndRefresh) onDragEndRefresh();

      if (onSaveLayout) {
        onSaveLayout(stage.toJSON());
      }

      if (setIsLayoutChanged) {
        setIsLayoutChanged(true);
      }
    };

    const getTargetId = (target: Konva.Node) => {
      if (target.id()) {
        return target.id();
      }
      if (target.parent) {
        return getTargetId(target.parent);
      }
      return '';
    };

    const getRoomName = (target: Konva.Node) => {
      if (target.attrs.text) {
        return target.attrs.text;
      }
      return target.parent?.children.find((child) => child.attrs.text)?.attrs.text;
    };

    const onClickElement = (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (e.target === e.target.getStage()) {
        setSelectedShapeName('');
        return;
      }

      const id = getTargetId(e.target);
      const roomName = getRoomName(e.target);

      setSelectedDetailName(roomName);

      setSelectedShapeName(id);

      const clickedOnTransformer = e.target.getParent()?.className === 'Transformer';
      if (clickedOnTransformer) {
        return;
      }

      // const name = e.target.name();

      // You'll need to define listRooms state or get it from props
      // const listRooms: any[] = []; // Replace with your actual rooms data
      // const room = listRooms.find(
      //   (r) => ('room' + r.name).toLocaleUpperCase() === name.toLocaleUpperCase(),
      // );

      // if (room) {
      //   setSelectedShapeName(id);
      //   setSelectedShape(e.target.index ? e.target : e.target.getParent());
      // } else {
      //   setSelectedShapeName('');
      //   setSelectedShape(null);
      // }
    };

    const handleChangeColor = (color: string) => {
      if (!selectedShapeName || !stageRef.current) return;

      const stage = stageRef.current.getStage();
      const node = stage.findOne(`#${selectedShapeName}`);

      if (node) {
        const isRoom = rooms.some((room) => room.id === selectedShapeName);

        if (!isRoom) return;

        const rect =
          node.getType() === 'Group'
            ? (node as any).findOne('Rect')
            : node.getType() === 'Rect'
            ? node
            : null;

        if (rect) {
          rect.fill(color);

          stage.batchDraw();

          if (onSaveLayout) {
            onSaveLayout(stage.toJSON());
          }
        }

        if (setIsLayoutChanged) {
          setIsLayoutChanged(true);
        }
      }
    };

    const handleSaveRoomName = (roomName: string) => {
      if (!selectedShapeName || !stageRef.current) return;

      const stage = stageRef.current.getStage();
      const node = stage.findOne(`#${selectedShapeName}`);

      if (node) {
        // Find the Text element within the Group
        const textNode =
          node.getType() === 'Group' ? (node as any).findOne('Text') : node.parent?.findOne('Text');

        if (textNode) {
          // Update the text content
          textNode.text(roomName);
          stage.batchDraw();

          // Update the selectedRoomName state to reflect the change
          setSelectedDetailName(roomName);
        }
      }

      if (setIsLayoutChanged) {
        setIsLayoutChanged(true);
      }

      if (onSaveLayout) {
        onSaveLayout(stage.toJSON());
      }
    };

    const deleteRoomOrRack = () => {
      if (!selectedShapeName || !stageRef.current) return;

      const stage = stageRef.current.getStage();
      const node = stage.findOne(`#${selectedShapeName}`);

      if (node) {
        node.remove();
        stage.batchDraw();
        setSelectedShapeName('');

        if (onSaveLayout) {
          onSaveLayout(stage.toJSON());
        }
      }

      if (selectedShapeName.startsWith('rack-')) {
        handleDeleteRack(selectedShapeName);
      }

      if (setIsLayoutChanged) {
        setIsLayoutChanged(true);
      }
    };

    const handleTransformEnd = (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (!selectedShapeName || !stageRef.current) return;

      const node: any = e.target;
      const stage = stageRef.current.getStage();
      const id = node.id();
      const isRack = id.startsWith('rack-');

      // Check for boundary violations and overlaps after transformation
      const boundingBox = node.getClientRect();
      const isOutsideBoundaries = isOutsideStageBoundaries(boundingBox, stage);

      // Check for overlaps
      const targetBounds = getBoundsFromBox(boundingBox);
      const isOverlapping = checkForOverlaps(id, targetBounds, isRack, rooms, racks, stage);

      // If there's a boundary violation or overlap, roll back to initial position
      if (isOutsideBoundaries || isOverlapping) {
        const initialPos = initialPositionsRef.current.get(id);

        if (initialPos) {
          resetToInitialPosition(
            node,
            initialPos,
            stage,
            onUpdateRackPosition,
            onUpdateRoomPosition,
          );
          if (onOutOfBoundsDrag) onOutOfBoundsDrag(isOverlapping);
          return;
        }
      }

      // Handle text scaling within groups
      if (node.getClassName() === 'Group') {
        const textNode = node.findOne('Text');
        if (textNode) {
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          textNode.scaleX(1 / scaleX);
          textNode.scaleY(1 / scaleY);

          verticalAlignTextCenterWithEllipsis(textNode, node.findOne('Rect'), scaleX);
        }
      }

      stage.batchDraw();

      if (onDragEndRefresh) onDragEndRefresh();

      if (onSaveLayout) {
        onSaveLayout(stage.toJSON());
      }

      if (setIsLayoutChanged) {
        setIsLayoutChanged(true);
      }
    };

    const adjustTextScales = () => {
      if (!stageRef.current) return;

      const stage = stageRef.current.getStage();
      const groups = stage.find('Group');

      groups.forEach((group: any) => {
        const textNode = group.findOne('Text');
        if (textNode) {
          const scaleX = group.scaleX();
          const scaleY = group.scaleY();

          textNode.scaleX(1 / scaleX);
          textNode.scaleY(1 / scaleY);

          verticalAlignTextCenterWithEllipsis(textNode, group.findOne('Rect'), scaleX);
        }
      });

      stage.batchDraw();
    };

    const isShowChangeColor = selectedShapeName !== '';
    const isEditRack = selectedShapeName.startsWith('rack-');

    const handleNavigateToRackDetail = (rackIdStr: string) => () => {
      const id = rackIdStr.replace('rack-', '');
      navigate(
        `${PATH.RACK_DETAIL.replace(
          ':rackId',
          id,
        )}?mode=0&limit=50&page=1&orderBy=name&orderDirection=ASC`,
      );
    };

    return (
      <>
        <Stage width={width} height={height} ref={stageRef} onClick={onClickElement}>
          <Layer>
            {selectedShapeName !== '' ? (
              <TransformerComponent
                handleTransformEnd={handleTransformEnd}
                selectedShapeName={selectedShapeName ? selectedShapeName : ''}
              />
            ) : null}

            {rooms.map((room) => (
              <RoomRect
                key={room.id}
                id={room.id}
                x={room.x}
                y={room.y}
                scaleX={room.scaleX}
                scaleY={room.scaleY}
                offsetX={room.offsetX}
                offsetY={room.offsetY}
                height={room.height}
                width={room.width}
                rotation={room.rotation}
                skewX={room.skewX}
                skewY={room.skewY}
                rectAttrs={room.rectAttrs}
                textAttrs={room.textAttrs}
                draggable={isEditMode}
                onDragMove={handleDragMove}
                onDragEnd={handleDragEnd}
              />
            ))}

            {racks.map((rack) => (
              <RackRect
                key={rack.id}
                id={rack.id}
                x={rack.x}
                y={rack.y}
                scaleX={rack.scaleX}
                scaleY={rack.scaleY}
                height={rack.height}
                width={rack.width}
                rotation={rack.rotation}
                offsetX={rack.offsetX}
                offsetY={rack.offsetY}
                skewX={rack.skewX}
                rectAttrs={rack.rectAttrs}
                textAttrs={rack.textAttrs}
                draggable={isEditMode}
                onDragMove={handleDragMove}
                onDragEnd={handleDragEnd}
                onClick={isEditMode ? undefined : handleNavigateToRackDetail(rack.id)}
              />
            ))}
          </Layer>
        </Stage>

        {isShowChangeColor ? (
          <ChangeColorComp
            onRoomNameChange={handleSaveRoomName}
            selectedDetailName={selectedDetailName}
            onChangeColor={handleChangeColor}
            onDeleteRoom={deleteRoomOrRack}
            isEditRack={isEditRack}
          />
        ) : null}
      </>
    );
  },
);
